// app/(routes)/news/page.tsx
"use client";

import Image from 'next/image';
import { useRouter } from 'next/navigation';

import { useAuthHook } from '@/app/auth/services_and_hooks/useAuthHook';
import { PublicNewsEdition } from '@/app/features/news/news.interface';
import { useNewsEditionsPublicHook } from '@/app/features/news/services_and_hooks/public/useNewsEditionsPublicHook';
import { SiteFooter } from '@/components/site-footer';
import { SiteHeader } from '@/components/site-header';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

import { NewsletterMarketingSection } from './components/NewsletterMarketingComponent';

const NEWS_CATEGORIES = [
  { id: 'all', name: 'All Categories' },
  { id: 'frontier_models', name: 'Frontier Models' },
  { id: 'ai_coding_dev', name: 'AI Coding & Dev' },
  { id: 'image_video_models', name: 'Image & Video' },
  { id: 'open_source', name: 'Open Source' },
  { id: 'agentic_automation', name: 'Agentic AI' },
  { id: 'voice_ai', name: 'Voice AI' },
  { id: 'physical_ai', name: 'Physical AI' },
  { id: 'policy_regulation', name: 'Policy & Regulation' },
  { id: 'ai_education', name: 'AI Education' },
  { id: 'ai_industry_news', name: 'Industry News' },
];

export default function NewsPage() {
  const router = useRouter();
  const { editions, isLoading, error } = useNewsEditionsPublicHook();
  const { user, profile, organizations } = useAuthHook();


  // Debug user state
  console.log('[NewsPage] User state:', { 
    user: user ? { id: user.id, email: user.email } : null,
    hasProfile: !!profile,
    organizationsCount: organizations?.length || 0
  });

  // Get user's primary organization
  const primaryOrganization = organizations?.find(org => 
    org.is_primary
  ) || organizations?.[0];

  const latestEdition = editions?.[0];
  const previousEditions = editions?.slice(1) || [];

  if (error) {
    return (
      <div className="min-h-screen bg-surface-light">
        <SiteHeader />
        <main className="flex-1 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Unable to Load Newsletter Editions
            </h2>
            <p className="text-gray-600 mb-8">
              We're having trouble loading the newsletter editions. Please try again later.
            </p>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "AI Newsletter Weekly",
            "description": "Stay updated with the latest AI developments curated and personalized weekly",
            "url": "https://howthef.ai/news",
            "mainEntity": {
              "@type": "ItemList",
              "name": "AI Newsletter Editions",
              "description": "Weekly curated AI news editions",
              "itemListElement": editions?.slice(0, 5).map((edition, index) => ({
                "@type": "NewsArticle",
                "position": index + 1,
                "headline": edition.title,
                "description": edition.summary,
                "datePublished": edition.published_at,
                "author": {
                  "@type": "Organization",
                  "name": "HowTheF"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "HowTheF",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://howthef.ai/logo.png"
                  }
                }
              })) || []
            }
          })
        }}
      />
      
      <div className="min-h-screen bg-surface-light">
        <SiteHeader />

        <main className="flex-1">
          {!user?.id ? (
            // Marketing page for unauthenticated users
            <NewsletterMarketingSection editions={editions || []} isLoading={isLoading} />
          ) : (
            // Existing authenticated experience
            <>
              {/* Hero Header - Clean & Simple */}
              <section className=" border-b border-gray-100">
                <div className="container mx-auto px-4 py-8">
                  <div className="text-center space-y-4 max-w-3xl mx-auto">
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                      AI News Weekly
                    </h1>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Have a hard time staying up to date with AI? Us too.<br />
                      So, this will read all AI news of the week, and based on your profile and business context, create a unique personalized newsletter tailored for you.
                    </p>
                  </div>
                </div>
              </section>
            </>
          )}

          {user?.id && (
            /* Main Content - Only show for authenticated users */
            <section className="py-12">
              <div className="container mx-auto px-4">
                {isLoading ? (
                  <div className="space-y-12">
                    {/* Latest Edition Skeleton */}
                    <div className="animate-pulse">
                      <div className="bg-layer-1 rounded-2xl shadow-lg overflow-hidden">
                        <div className="h-80 bg-gray-200" />
                        <div className="p-8 space-y-4">
                          <div className="h-8 bg-gray-200 rounded w-3/4" />
                          <div className="h-4 bg-gray-200 rounded w-full" />
                          <div className="h-4 bg-gray-200 rounded w-2/3" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Previous Editions Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="bg-layer-1 rounded-xl shadow-sm overflow-hidden">
                            <div className="h-48 bg-gray-200" />
                            <div className="p-6 space-y-3">
                              <div className="h-6 bg-gray-200 rounded w-3/4" />
                              <div className="h-4 bg-gray-200 rounded w-full" />
                              <div className="h-4 bg-gray-200 rounded w-1/2" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : !editions || editions.length === 0 ? (
                  <div className="text-center py-16">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No editions found</h3>
                    <p className="text-gray-600">Try adjusting your filters or search terms.</p>
                  </div>
                ) : (
                  <div className="space-y-16">
                    {/* Latest Edition - Featured */}
                    {latestEdition && (
                      <FeaturedEditionCard edition={latestEdition} user={user} router={router} />
                    )}

                    {/* Previous Editions */}
                    {previousEditions.length > 0 && (
                      <div className="space-y-8">
                        <div className="flex items-center justify-between">
                          <h2 className="text-3xl font-bold text-gray-900">Previous Editions</h2>
                          <span className="text-gray-500">{previousEditions.length} editions</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {previousEditions.map((edition) => (
                            <EditionCard key={edition.edition_id || edition.edition_date} edition={edition} user={user} router={router} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}
        </main>

        <SiteFooter />
      </div>
    </>
  );
}

// Featured Edition Component (Latest/Hero)
function FeaturedEditionCard({ edition, user, router }: { edition: PublicNewsEdition; user: any; router: any }) {
  const formattedDate = new Date(edition.edition_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleClick = () => {
    if (user?.id) {
      const url = `/news/${edition.edition_date}?edition_id=${edition.edition_id}&user_id=${user.id}`;
      console.log('🚀 Featured edition clicked:', {
        edition_date: edition.edition_date,
        edition_id: edition.edition_id,
        user_id: user.id,
        full_url: url
      });
      window.location.href = url;
    }
  };

  return (
    <Card 
      className={`overflow-hidden shadow-lg bg-layer-1 border-0 ${user?.id ? 'cursor-pointer hover:shadow-2xl transition-shadow duration-300' : 'cursor-not-allowed opacity-75'}`}
      onClick={handleClick}
    >
      <div className="lg:flex">
        {/* Image Placeholder */}
        <div className="lg:w-1/2">
          <div className="relative h-80 lg:h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            {edition.hero_image_url ? (
              <Image 
                src={edition.hero_image_url} 
                alt={edition.title}
                fill
                className="object-cover"
              />
            ) : (
              <div className="text-center text-gray-700 p-8">
                <h3 className="text-2xl font-bold mb-2">AI Weekly</h3>
                <p className="text-gray-500">Latest Edition</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                Latest Edition
              </Badge>
              <span className="text-gray-500 text-sm">{formattedDate}</span>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              {edition.title}
            </h2>
            
            <p className="text-gray-600 text-lg leading-relaxed">
              {edition.summary}
            </p>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{edition.story_count} stories</span>
              <div className="w-1 h-1 bg-gray-300 rounded-full" />
              <span>5-7 min read</span>
              {edition.is_personalized && (
                <>
                  <div className="w-1 h-1 bg-gray-300 rounded-full" />
                  <span className="text-primary font-medium">Personalized for you</span>
                </>
              )}
            </div>
            
            {!user?.id && (
              <p className="text-sm text-gray-500 italic">Please sign in to read this edition</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// Regular Edition Card Component
function EditionCard({ edition, user, router }: { edition: PublicNewsEdition; user: any; router: any }) {
  const formattedDate = new Date(edition.edition_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const cardContent = (
    <>
      {/* Image Placeholder */}
      <div className="relative h-36 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-primary/10 group-hover:to-secondary/10 transition-all duration-300">
        {edition.cover_image_url ? (
          <Image 
            src={edition.cover_image_url} 
            alt={edition.title}
            fill
            className="object-cover"
          />
        ) : (
          <div className="text-center text-gray-500 group-hover:text-primary transition-colors">
            <div className="w-8 h-8 mx-auto mb-2 bg-gray-300 rounded" />
            <p className="text-sm font-medium">{formattedDate}</p>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{formattedDate}</span>
          {edition.is_personalized && (
            <Badge variant="outline" className="text-xs border-primary/30 text-primary">
              Personalized
            </Badge>
          )}
        </div>
        
        <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
          {edition.title}
        </h3>
        
        <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
          {edition.summary}
        </p>
        
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
          <span>{edition.story_count} stories</span>
          <span>3-5 min read</span>
        </div>
      </div>
    </>
  );

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-layer-1 group border-0 shadow-md">
      {user?.id ? (
        <div 
          className="cursor-pointer block"
          onClick={(e) => {
            e.preventDefault();
            const url = `/news/${edition.edition_date}?edition_id=${edition.edition_id}&user_id=${user.id}`;
            console.log('🔥 Edition card clicked:', {
              edition_date: edition.edition_date,
              edition_id: edition.edition_id,
              user_id: user.id,
              full_url: url
            });
            console.log('🔥 Attempting navigation to:', url);
            
            // Force navigation
            window.location.href = url;
          }}
        >
          {cardContent}
        </div>
      ) : (
        <div className="opacity-50 cursor-not-allowed">
          {cardContent}
        </div>
      )}
    </Card>
  );
}